// src/pages/b2b/Favourite.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Tooltip,
  Paper,
  Divider,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "../../../Auth/AuthContext";
import { useGlobalSnackbar } from "../../../../GlobalFunction/GlobalSnackbarProvider";
import { FavouriteAPI } from "../../../../../api/Favourite";
import { InquiryAPI } from "../../../../../api/Inquiry";
import { resolveAssetUrl } from "../../../../../lib/http";
import { resolveCanonicalUserID } from "../../../../../Utils/UserIdentity";
import ProductCard from "../../../../GlobalFunction/ProductCard";
import {
  getLangFromCookie,
  LANG_COOKIE,
} from "../../../../../Utils/LangCookie";

/* ------------------------------ i18n ------------------------------ */

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

const UI = {
  ZH: {
    title: "我的收藏",
    searchPlaceholder: "輸入關鍵字即時篩選收藏…",
    clear: "清除",
    loading: "讀取中…",
    loadError: "載入收藏失敗",
    empty: "目前沒有收藏商品。",
    perPageLabel: (n) => `每頁 ${n}`,
    brandAll: "全部品牌",
    wishlistRemove: "移除收藏",
    wishlistAdd: "加入收藏",
    addInquiry: "加入詢價",
    adding: "加入中…",
    added: "已加入詢價清單！",
    addFailPrefix: (msg) => `加入詢價失敗：${msg}`,
    loginFirst: "請先登入。",
  },
  EN: {
    title: "My Favourites",
    searchPlaceholder: "search...",
    clear: "Clear",
    loading: "Loading…",
    loadError: "Failed to load favourites",
    empty: "No favourite products yet.",
    perPageLabel: (n) => `${n} items`,
    brandAll: "All brands",
    wishlistRemove: "Remove from wishlist",
    wishlistAdd: "Add to wishlist",
    addInquiry: "Add to inquiry",
    adding: "Adding…",
    added: "Added to inquiry list!",
    addFailPrefix: (msg) => `Failed to add to inquiry list: ${msg}`,
    loginFirst: "Please log in first.",
  },
  JP: {
    title: "お気に入り",
    searchPlaceholder: "キーワードを入力してお気に入りを絞り込む…",
    clear: "クリア",
    loading: "読み込み中…",
    loadError: "お気に入りを読み込めませんでした",
    empty: "お気に入りの商品はまだありません。",
    perPageLabel: (n) => `1ページ ${n} 件`,
    brandAll: "すべてのブランド",
    wishlistRemove: "お気に入りから削除",
    wishlistAdd: "お気に入りに追加",
    addInquiry: "見積りに追加",
    adding: "追加中…",
    added: "見積リストに追加しました！",
    addFailPrefix: (msg) => `追加に失敗しました：${msg}`,
    loginFirst: "ログインしてください。",
  },
};

function getCurrentLang() {
  const v = getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";
  return normalizeLang(v);
}

/* ------------------------------ helpers ------------------------------ */

function resolveUserIdAny(user) {
  return resolveCanonicalUserID(user);
}

function safeTrim(v) {
  return String(v ?? "").trim();
}

function buildB2BDetailPathFromFavouriteRow(row) {
  const parentKey = safeTrim(row?.parentID);
  const variantKey = safeTrim(row?.productID);

  if (!parentKey) return "";

  let url = `/商品資訊/${encodeURIComponent(parentKey)}`;

  if (variantKey && variantKey !== parentKey) {
    url += `?variant=${encodeURIComponent(variantKey)}`;
  }
  return url;
}

function getBrandNameFromFavouriteRow(row) {
  // backend row may provide:
  // row.category.brandName OR row.category.brand OR row.brandName etc.
  const c =
    row?.category && typeof row.category === "object" ? row.category : null;

  const v =
    row?.brandName ||
    row?.brand ||
    row?.Brand ||
    c?.brandName ||
    c?.brand ||
    c?.Brand ||
    c?.name ||
    "";

  const s = String(v || "").trim();
  const lower = s.toLowerCase();
  if (!s) return "";
  if (lower === "-" || lower.startsWith("-")) return "";
  if (lower === "brand") return "";
  return s;
}

function pickNameForSearch(row) {
  const name = safeTrim(row?.name);
  const spec = safeTrim(row?.variants?.["規格"]);
  const wc = safeTrim(row?.variants?.["重量/容量"]);
  const dim = safeTrim(row?.variants?.["尺寸"]);
  const brand = getBrandNameFromFavouriteRow(row);
  return `${brand} ${name} ${spec} ${wc} ${dim}`.trim();
}

function pickImage(row) {
  const img =
    row?.image || (Array.isArray(row?.images) ? row.images[0] : "") || "";
  const s = safeTrim(img);
  return s ? resolveAssetUrl(s) : "/img/empty.webp";
}

/* ------------------------------ page ------------------------------ */

export default function FavouritePageB2B() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { showSnackbar } = useGlobalSnackbar();
  const { user, loading: authLoading } = useAuth();

  const uid = useMemo(() => safeTrim(resolveUserIdAny(user)), [user]);

  const [lang, setLang] = useState(getCurrentLang());
  const t = UI[lang] || UI.ZH;

  useEffect(() => {
    const onLangChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("app:languageChanged", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);

  const PAGE_MAX = 100;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // controls (like ProductInfoPage)
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list"); // ✅ default list to match your screenshot
  const [brand, setBrand] = useState("__ALL__");
  const [pageSize, setPageSize] = useState(10);
  const pageSizeClamped = Math.min(Math.max(1, pageSize), PAGE_MAX);
  const [page, setPage] = useState(1);

  useEffect(() => setPage(1), [search, brand, view, pageSizeClamped]);

  useEffect(() => {
  let ignore = false;

  async function fetchFavs() {
    if (authLoading) return;

    setLoading(true);
    setError("");

    try {
      if (!uid) {
        if (!ignore) {
          setItems([]);
          setLoading(false);
        }
        return;
      }

      const list = await FavouriteAPI.list(uid, { lang });
      if (!ignore) {
        setItems(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      console.error("Favourite list load failed:", e);
      if (!ignore) {
        setItems([]);
        setError(t.loadError);
      }
    } finally {
      if (!ignore) setLoading(false);
    }
  }

  fetchFavs();
  return () => {
    ignore = true;
  };
}, [authLoading, uid, lang, t.loadError]);

  const brandOptions = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      const b = getBrandNameFromFavouriteRow(it);
      if (b) set.add(b);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    const byBrand =
      brand === "__ALL__"
        ? items
        : items.filter((x) => getBrandNameFromFavouriteRow(x) === brand);

    if (!q) return byBrand;

    return byBrand.filter((x) =>
      pickNameForSearch(x).toLowerCase().includes(q),
    );
  }, [items, search, brand]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSizeClamped));

  const paged = useMemo(() => {
    const start = (page - 1) * pageSizeClamped;
    return filtered.slice(start, start + pageSizeClamped);
  }, [filtered, page, pageSizeClamped]);

  const goDetail = (row) => {
    const to = buildB2BDetailPathFromFavouriteRow(row);
    if (!to) return;
    navigate(to, {
      state: {
        from: loc.pathname,
        fromList: "favourite",
      },
    });
  };

  const removeFavourite = async (row) => {
    const productID = safeTrim(row?.productID);
    if (!uid || !productID) return;

    // ✅ Optimistic UI
    setItems((prev) =>
      (Array.isArray(prev) ? prev : []).filter(
        (x) => safeTrim(x?.productID) !== productID,
      ),
    );

    // ✅ Tell navbar (and any other listener) to refresh badge count
    window.dispatchEvent(
      new CustomEvent("favourite:updated", {
        detail: { reason: "removed", userID: uid, productID },
      }),
    );

    try {
      await FavouriteAPI.remove(uid, productID);

      // ✅ Optional: dispatch again after success (safe for eventual consistency)
      window.dispatchEvent(
        new CustomEvent("favourite:updated", {
          detail: { reason: "removed_ok", userID: uid, productID },
        }),
      );
    } catch (e) {
      console.error("Remove favourite failed:", e);

      // rollback
      setItems((prev) => {
        const exists = (Array.isArray(prev) ? prev : []).some(
          (x) => safeTrim(x?.productID) === productID,
        );
        if (exists) return prev;
        return [row, ...(Array.isArray(prev) ? prev : [])];
      });

      // ✅ rollback event so navbar count can recover
      window.dispatchEvent(
        new CustomEvent("favourite:updated", {
          detail: { reason: "remove_failed_rollback", userID: uid, productID },
        }),
      );

      showSnackbar(
        e?.response?.data?.error || e?.message || "Network error",
        "error",
      );
    }
  };

  const addToInquiry = async (row) => {
    try {
      if (!user) {
        showSnackbar(t.loginFirst, "warning");
        navigate("/auth", {
          replace: false,
          state: { redirectTo: loc.pathname },
        });
        return;
      }

      const productID = safeTrim(row?.productID);
      if (!productID) return;

      await InquiryAPI.createInquiry({
        userID: uid,
        items: [{ productID: String(productID), qty: 1 }],
        lang: normalizeLang(lang),
      });

      showSnackbar(t.added, "success");
      window.dispatchEvent(
        new CustomEvent("inquiry:updated", {
          detail: { reason: "added", productID },
        }),
      );
    } catch (e) {
      console.error(e);
      showSnackbar(
        t.addFailPrefix(e?.response?.data?.error || e.message),
        "error",
      );
    }
  };

  // ✅ For grid view, we can reuse ProductCard fully.
  // ✅ For list view, we redesign to match your screenshot (image left, text middle, icons right).
  const FavouriteRowList = ({ row }) => {
    const name = safeTrim(row?.name);
    const img = pickImage(row);

    // optional: show price if backend later provides
    const price =
      safeTrim(row?.price) ||
      safeTrim(row?.Price) ||
      safeTrim(row?.main?.price) ||
      safeTrim(row?.main?.Price) ||
      safeTrim(row?.main?.售價) ||
      safeTrim(row?.main?.價格) ||
      "";

    return (
      <Paper
        variant="outlined"
        onClick={() => goDetail(row)}
        sx={{
          p: 1.5,
          borderRadius: 2,
          cursor: "pointer",
          "&:hover": { boxShadow: 1 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "88px 1fr 76px", sm: "110px 1fr 90px" },
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* left image */}
          <Box
            component="img"
            src={img}
            alt={name}
            onError={(e) => {
              if (e.currentTarget.src.endsWith("/img/empty.webp")) return;
              e.currentTarget.src = "/img/empty.webp";
            }}
            sx={{
              width: { xs: 88, sm: 110 },
              height: { xs: 88, sm: 110 },
              objectFit: "contain",
              bgcolor: "#fff",
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              p: 0.5,
            }}
          />

          {/* middle text */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                lineHeight: 1.35,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {name}
            </Typography>

            {!!price && (
              <Typography
                sx={{ mt: 0.5, color: "error.main", fontWeight: 800 }}
              >
                {String(price).startsWith("NT") ? price : `NT$${price}`}
              </Typography>
            )}
          </Box>

          {/* right icons */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={0.75}
          >
            <Tooltip title={t.wishlistRemove}>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeFavourite(row);
                }}
                size="small"
                sx={{ p: 0.75 }}
              >
                <FavoriteIcon sx={{ color: "red" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={t.addInquiry}>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToInquiry(row);
                }}
                size="small"
                sx={{ p: 0.75 }}
              >
                <ShoppingCartOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fffffc" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1080,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        {/* header + controls (like ProductInfoPage) */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.25}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {t.title}
          </Typography>

          {/* desktop controls */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 1.25,
              flexWrap: "nowrap",
            }}
          >
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchPlaceholder}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch("")}
                      aria-label="clear search"
                      edge="end"
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <ToggleButtonGroup
              exclusive
              size="medium"
              value={view}
              onChange={(_, v) => v && setView(v)}
            >
              <ToggleButton value="grid">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select
                value={pageSizeClamped}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <MenuItem value={10}>{t.perPageLabel(10)}</MenuItem>
                <MenuItem value={25}>{t.perPageLabel(25)}</MenuItem>
                <MenuItem value={50}>{t.perPageLabel(50)}</MenuItem>
                <MenuItem value={75}>{t.perPageLabel(75)}</MenuItem>
                <MenuItem value={100}>{t.perPageLabel(100)}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={brand}
                onChange={(e) => setBrand(String(e.target.value))}
                displayEmpty
                renderValue={(v) =>
                  v === "__ALL__" ? `${t.brandAll}` : `${v}`
                }
              >
                <MenuItem value="__ALL__">{t.brandAll}</MenuItem>
                {brandOptions.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* mobile controls */}
          <Box sx={{ display: { xs: "block", sm: "none" }, width: "100%" }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchPlaceholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch("")}
                      aria-label="clear search"
                      edge="end"
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Box
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "nowrap",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                pb: 0.25,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0,0,0,.18)",
                  borderRadius: 999,
                },
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="medium"
                value={view}
                onChange={(_, v) => v && setView(v)}
                sx={{ flex: "0 0 auto" }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>

              <FormControl
                size="small"
                sx={{ minWidth: 100, flex: "0 0 auto" }}
              >
                <Select
                  value={pageSizeClamped}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <MenuItem value={8}>{t.perPageLabel(8)}</MenuItem>
                  <MenuItem value={20}>{t.perPageLabel(20)}</MenuItem>
                  <MenuItem value={40}>{t.perPageLabel(40)}</MenuItem>
                  <MenuItem value={60}>{t.perPageLabel(60)}</MenuItem>
                  <MenuItem value={80}>{t.perPageLabel(80)}</MenuItem>
                  <MenuItem value={100}>{t.perPageLabel(100)}</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: 120, flex: "0 0 auto" }}
              >
                <Select
                  value={brand}
                  onChange={(e) => setBrand(String(e.target.value))}
                  displayEmpty
                  renderValue={(v) =>
                    v === "__ALL__" ? `${t.brandAll}` : `${v}`
                  }
                >
                  <MenuItem value="__ALL__">{t.brandAll}</MenuItem>
                  {brandOptions.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* content */}
        {loading && (
          <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            {t.loading}
          </Box>
        )}

        {!loading && !!error && (
          <Box sx={{ p: 4, textAlign: "center", color: "error.main" }}>
            {error}
          </Box>
        )}

        {!loading && !error && (
          <>
            {filtered.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
                {t.empty}
              </Box>
            )}

            {filtered.length > 0 && (
              <>
                {view === "grid" ? (
                  <Box
                    sx={{
                      display: "grid",
                      gap: { xs: 2, sm: 3, md: 4 },
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {paged.map((row) => {
                      const to = buildB2BDetailPathFromFavouriteRow(row);

                      // ✅ Use ProductCard in grid mode (already supports favourite toggle)
                      // But favourites page needs “remove” (and keep list synced), so we override behaviour:
                      // - We still allow the heart in ProductCard, but we also sync list state using onFavouriteChange.
                      const productForCard = {
                        productID: safeTrim(row?.productID),
                        parentID: safeTrim(row?.parentID),
                        name: safeTrim(row?.name),
                        image: pickImage(row),
                        images: Array.isArray(row?.images) ? row.images : [],
                        favourite: 1,
                        category: row?.category || null,
                      };

                      return (
                        <ProductCard
                          key={productForCard.productID}
                          product={productForCard}
                          to={to}
                          view="grid"
                          titleLines={2}
                          showFavourite
                          userID={uid}
                          onFavouriteChange={(next, pid) => {
                            if (!next) {
                              const productID = safeTrim(pid);

                              setItems((prev) =>
                                (Array.isArray(prev) ? prev : []).filter(
                                  (x) => safeTrim(x?.productID) !== productID,
                                ),
                              );

                              // ✅ update navbar badge
                              window.dispatchEvent(
                                new CustomEvent("favourite:updated", {
                                  detail: {
                                    reason: "removed_from_grid",
                                    userID: uid,
                                    productID,
                                  },
                                }),
                              );
                            } else {
                              // (Optional) if ProductCard can add favourite from here
                              window.dispatchEvent(
                                new CustomEvent("favourite:updated", {
                                  detail: {
                                    reason: "added_from_grid",
                                    userID: uid,
                                    productID: safeTrim(pid),
                                  },
                                }),
                              );
                            }
                          }}
                          onClickCard={(e) => {
                            if (
                              e &&
                              (e.metaKey ||
                                e.ctrlKey ||
                                e.shiftKey ||
                                e.button === 1)
                            )
                              return;
                            if (e && e.preventDefault) e.preventDefault();
                            navigate(to);
                          }}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    {paged.map((row) => (
                      <FavouriteRowList
                        key={safeTrim(row?.productID) || Math.random()}
                        row={row}
                      />
                    ))}
                  </Stack>
                )}

                <Stack alignItems="center" sx={{ mt: 3 }}>
                  <Pagination
                    page={page}
                    count={Math.max(1, totalPages)}
                    onChange={(_, v) => setPage(v)}
                    shape="rounded"
                    color="primary"
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    {`Page ${page} / ${totalPages} (${pageSizeClamped} items per page)`}
                  </Typography>
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
